import React from "react";

const fmtUa = (v) =>
    Number(Math.round(Number(v) || 0)).toLocaleString("uk-UA", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });

/** Два знаки як у блоці лімітів НБУ (ForeignSpendAccordion). */
const fmtUsd = (v) =>
    Number(v || 0).toLocaleString("uk-UA", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

/**
 * Смуга на кшталт «Закордонні витрати (ліміт НБУ)»: факт до сьогодні vs прогноз на кінець місяця.
 * @param {string} [heading] — заголовок зліва (за замовчуванням загальний ТО)
 * @param {boolean} [showMonthInHeading] — показувати (monthTitle) у заголовку
 * @param {boolean} [compact] — коротший підпис для карток по джерелах
 * @param {number} [factShareOfMonthPct] — разом із `compact`: частка фактового ТО з початку місяця по джерелу (%), як у сегменті діаграми
 */
export default function MonthlyTurnoverProgressBar({
    model,
    currency,
    usdSellRate,
    heading = "📊 Місячний товарообіг",
    showMonthInHeading = true,
    compact = false,
    expandVertically = false,
    factShareOfMonthPct,
}) {
    if (!model || typeof model !== "object") return null;

    const {
        totalSoFar,
        projectedMonthEnd,
        avgDailyRounded,
        remainingDays,
        elapsedDays,
        daysInMonth,
        percentTowardProjection,
        monthTitle,
    } = model;

    const toUsd = (uah) => {
        const r = Number(usdSellRate);
        if (!Number.isFinite(r) || r <= 0 || currency !== "UAH") return null;
        return uah / r;
    };

    const formatMoneyUa = (uah) =>
        `${fmtUa(uah)} грн${
            currency === "UAH" && usdSellRate ? ` (~$${fmtUsd(toUsd(uah))})` : ""
        }`;

    const remainingToProjection = Math.max(0, Math.round(projectedMonthEnd) - Math.round(totalSoFar));
    const remainingUsd =
        currency === "UAH" && usdSellRate ? toUsd(remainingToProjection) : null;
    const remainingUsdParen =
        remainingUsd != null && Number.isFinite(remainingUsd) ? ` (~$${fmtUsd(remainingUsd)})` : "";

    return (
        <div
            className={`rounded border bg-light ${compact ? "w-100 p-2 mb-0 h-100" : "p-3 mb-3"}${
                compact && expandVertically ? " d-flex flex-column" : ""
            }`}
        >
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                <strong className="text-body">
                    {heading}
                    {compact &&
                    typeof factShareOfMonthPct === "number" &&
                    Number.isFinite(factShareOfMonthPct) ? (
                        <span className="fw-normal text-muted ms-1">
                            ({factShareOfMonthPct.toFixed(1)}% за місяць)
                        </span>
                    ) : null}
                    {showMonthInHeading && monthTitle ? (
                        <span className="fw-normal text-muted ms-1">({monthTitle})</span>
                    ) : null}
                </strong>
                <span className="small text-end">
                    <span className="text-secondary">{formatMoneyUa(totalSoFar)}</span>
                    <span className="text-secondary"> {" / "}</span>
                    <strong className="text-body">{formatMoneyUa(projectedMonthEnd)}</strong>
                    {!compact ? (
                        <span className="d-block mt-1 text-body-secondary fst-italic fw-normal">
                            Прогноз: поточний + середньодобовий × решта днів місяця
                        </span>
                    ) : null}
                </span>
            </div>

            <div className="progress mt-2" style={{ height: 8 }}>
                <div
                    className={`progress-bar ${
                        percentTowardProjection >= 92
                            ? "bg-danger"
                            : percentTowardProjection >= 80
                              ? "bg-warning"
                              : "bg-success"
                    }`}
                    style={{ width: `${Math.min(100, Math.max(0, percentTowardProjection))}%` }}
                    role="progressbar"
                    aria-valuenow={Math.round(percentTowardProjection)}
                    aria-valuemin={0}
                    aria-valuemax={100}
                />
            </div>

            {compact ? (
                <div
                    className={`small mt-2 text-muted d-flex flex-wrap justify-content-between gap-1${
                        expandVertically ? " mt-auto pt-2" : ""
                    }`}
                >
                    <span>
                        Сер. день: <strong>{formatMoneyUa(avgDailyRounded)}</strong>
                    </span>
                    <span className="fw-normal text-muted">
                        До прогнозу: {fmtUa(remainingToProjection)} грн{remainingUsdParen}
                    </span>
                </div>
            ) : (
                <>
                    <div className="small mt-2 text-muted d-flex flex-wrap justify-content-between gap-2">
                        <span>
                            Середньодобовий: <strong>{formatMoneyUa(avgDailyRounded)}</strong>
                            {elapsedDays ? (
                                <span className="ms-1">
                                    (за {elapsedDays}&nbsp;
                                    {elapsedDays === 1
                                        ? "день"
                                        : elapsedDays < 5
                                          ? "дні"
                                          : "днів"}{" "}
                                    з початку місяця)
                                </span>
                            ) : null}
                        </span>
                        <span>
                            Днів залишилось (після сьогодні):{" "}
                            <strong>{remainingDays}</strong> / {daysInMonth} календарних
                        </span>
                    </div>
                    <div className="small mt-1 text-muted fw-normal">
                        Залишилось до прогнозу: {fmtUa(remainingToProjection)} грн{remainingUsdParen}
                    </div>
                </>
            )}
        </div>
    );
}
